import { useContext, useState } from 'react';
import { AuthContext, ViewContext, useNavigate } from 'components/lib';
import { Box, Modal } from '@mui/material';
import { UMButton } from '@urbanmetrix/um-kit';
import { listOrganizations, setActiveOrganization } from 'lib/auth-client';

export function NotFound() {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  const [organizationModalOpen, setOrganizationModalOpen] = useState(false);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const [organizations, setOrganizations] = useState<Array<{ id: string | number; name?: string }>>([]);
  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;

  async function openOrganizationModal() {
    const nextOrganizations = authContext.isAuthenticated ? await listOrganizations() : [];
    setOrganizations(Array.isArray(nextOrganizations) ? nextOrganizations : []);
    setOrganizationModalOpen(true);
  }

  async function handleSwitchOrganization(organizationId: string | number) {
    if (!organizationId || String(organizationId) === String(activeOrganizationId)) {
      setOrganizationModalOpen(false);
      return;
    }

    try {
      setIsSwitchingOrganization(true);
      viewContext?.setLoading?.(true);
      await setActiveOrganization(organizationId);
      await authContext.refreshSession?.();
      setOrganizationModalOpen(false);
      navigate('/dashboard');
    } finally {
      setIsSwitchingOrganization(false);
      viewContext?.setLoading?.(false);
    }
  }

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '18vh 20px 40px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '720px',
            textAlign: 'center',
            padding: '48px 40px',
            borderRadius: '24px',
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 18px 50px rgba(31, 41, 55, 0.12)',
          }}
        >
          <h1 style={{ margin: 0, fontSize: '32px', lineHeight: 1.2, color: '#1f2937', fontWeight: 700 }}>
            Page not found
          </h1>
          <p style={{ margin: '18px 0 0', fontSize: '18px', lineHeight: 1.6, color: '#4b5563' }}>
            The page you&apos;re looking for doesn&apos;t exist in this organization. Are you sure you&apos;re looking
            in the right organization?
          </p>

          <div
            style={{
              marginTop: '28px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <UMButton text="Go to dashboard" color="red" onClick={() => navigate('/dashboard')} />
            <UMButton text="Switch Organization" color="blue" variant="outlined" onClick={openOrganizationModal} />
          </div>
        </div>
      </div>

      <Modal open={organizationModalOpen} onClose={() => setOrganizationModalOpen(false)}>
        <Box className="organization-switch-modal">
          <div className="organization-switch-modal__header">
            <div className="organization-switch-modal__title">Switch organization</div>
            <div className="organization-switch-modal__subtitle">Choose one of your organizations.</div>
          </div>
          <div className="organization-switch-modal__list">
            {organizations.map((organization) => {
              const isActive = String(organization.id) === String(activeOrganizationId);
              return (
                <button
                  type="button"
                  key={organization.id}
                  className={`organization-switch-modal__option${isActive ? ' organization-switch-modal__option--active' : ''}`}
                  disabled={isActive || isSwitchingOrganization}
                  onClick={() => handleSwitchOrganization(organization.id)}
                >
                  <div className="organization-switch-modal__option-title">{organization.name || 'Organization'}</div>
                  {isActive && <div className="organization-switch-modal__option-subtitle">Active organization</div>}
                </button>
              );
            })}
          </div>
        </Box>
      </Modal>
    </>
  );
}
